import React, { useEffect, useState } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { usePopper } from 'react-popper';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from '@ais-ui/ui-core';

interface LayerMouseOverEvent extends CustomEvent {
  detail: {
    properties: Record<string, any>;
    position: {
      x: number;
      y: number;
    };
  };
}

interface LayerMouseOutEvent extends CustomEvent {
  detail: {
    previousFeatureId: string;
  };
}

interface LayerPopoverProps {
  properties: Record<string, any>;
  position: { x: number; y: number };
}

const LayerPopover = ({ properties, position }: LayerPopoverProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const name = properties.name || 'Layer Details';

  const formattedProperties = Object.entries(properties)
    .filter(
      ([key, value]) =>
        !key.startsWith('_') &&
        key !== 'geometry' &&
        key !== 'name' &&
        value !== undefined &&
        value !== ''
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const [virtualReference] = useState(() => ({
    getBoundingClientRect: () =>
      ({
        top: position.y,
        left: position.x,
        bottom: position.y,
        right: position.x,
        width: 0,
        height: 0,
      } as DOMRect),
  }));

  const { styles } = usePopper(virtualReference, popperElement, {
    strategy: 'fixed',
    placement: 'right',
  });

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  if (!position) return null;

  return (
    <Card
      ref={setPopperElement}
      style={styles.popper}
      className="mx-6 p-0 min-w-[128px]"
    >
      <CardHeader className="p-3">
        <CardTitle className="text-sm">{name}</CardTitle>
      </CardHeader>

      <CardContent className="pt-0 space-y-2 px-3">
        <Separator />
        <div className="space-y-1">
          {Object.entries(formattedProperties).map(([key, value]) => (
            <div key={key} className="grid grid-cols-2 gap-2">
              <span className="text-sm font-medium text-gray-500">{key}:</span>
              <span className="text-sm whitespace-pre-wrap font-mono">
                {renderValue(value)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const LayerHover = () => {
  const { map } = useOpenLayersContext();
  const [hoverState, setHoverState] = useState<{
    properties: Record<string, any>;
    position: { x: number; y: number };
  } | null>(null);

  useEffect(() => {
    const handleMouseOver = (event: Event) => {
      const { properties, position } = (event as LayerMouseOverEvent).detail;
      setHoverState({ properties, position });
    };

    const handleMouseOut = () => {
      setHoverState(null);
    };

    const mapElement = map.getTargetElement();

    mapElement?.addEventListener('layer-mouseover', handleMouseOver);
    mapElement?.addEventListener('layer-mouseout', handleMouseOut);

    return () => {
      mapElement?.removeEventListener('layer-mouseover', handleMouseOver);
      mapElement?.removeEventListener('layer-mouseout', handleMouseOut);
    };
  }, [map]);

  if (!hoverState) {
    return null;
  }

  return (
    <LayerPopover
      properties={hoverState.properties}
      position={hoverState.position}
    />
  );
};
